import React from "react";
import { CoatingSwatch } from "../../../../components/CoatingColor";
import {
  ageBadge, fmtDate, fmtQty, groupPalletIds, groupSelection, isOtherOrderPallet, jobLineNotes,
  linkedPalletNames, ownShareDone, allocatedAwayText, allocationLines, crossSoShareLine, palletPackedTotal,
} from "../../../../features/outsourcing/coatingData";
import { earliest, palletTickState } from "../../../../features/outsourcing/palletBuckets";

// The phone Outsourcing tab's To send list, pallet first (the desktop's Staging tab: CoatingManagementPage
// renderStagingTab): each order, its pallets with ONE tick each and their total, the parts on each with one total,
// the jobs under it; tasks on no pallet yet are a row each with their own tick. Layout only: the lists and rules
// are features/outsourcing/coatingData (+ palletBuckets), the writes coatingActions (through OutsourcingTab).

function PartImage({ src, alt }) {
  return src ? <img className="jq-out-part-img" src={src} alt={alt || "Part"} /> : null;
}

function PartName({ t }) {
  return t.assembly_name ? (
    <>
      <div className="fw-bold"><span className="jq-tag me-1">ASSY</span>{t.assembly_name}</div>
      <div className="small jq-muted">{t.product_id}{t.part_description ? ` — ${t.part_description}` : ""}</div>
    </>
  ) : (
    <>
      <div className="fw-bold">{t.product_id || "—"}</div>
      {t.part_description ? <div className="small jq-muted">{t.part_description}</div> : null}
    </>
  );
}

function FinishLine({ t, vendorName, due, mixed }) {
  return (
    <div className="small jq-muted d-flex flex-wrap gap-2 mt-1">
      {mixed && t.process_label ? <span className="jq-tag">{t.process_label}</span> : null}
      {t.color_code ? <CoatingSwatch code={t.color_code} name={t.color_name} hex={t.color_hex} />
        : <span className="text-danger">color not set</span>}
      <span>{vendorName || "no vendor — picked on the paper"}</span>
      {due ? <span>due {fmtDate(due)}</span> : null}
    </div>
  );
}

function JobLine({ line }) {
  const { t, qty, share } = line;
  const notes = jobLineNotes(line);
  return (
    <div className="jq-out-job" data-testid={share ? `phone-out-share-${t.task_id}-${share.sales_order_id}` : `phone-out-job-${t.task_id}`}>
      <span className="fw-semibold">#{t.master_job_num}</span>
      {t.set_identifier ? <span className="jq-tag">SET {t.set_identifier}</span> : null}
      {share ? <span className="jq-tag jq-tag-warn">↪ from {t.sales_order_number || "another order"}</span> : null}
      <span>{fmtQty(qty)}</span>
      {notes.length ? <span className="jq-out-job-notes">({notes.join(" · ")})</span> : null}
    </div>
  );
}

function PalletCard({ g, buckets, p, selected, busy, mixed, onTick, onFind, onMakePaper }) {
  const otherSo = isOtherOrderPallet(p.palletOrder, g.sales_order_id);
  const state = palletTickState(p, selected);
  const linked = linkedPalletNames(buckets, p);
  const anyAssembly = p.parts.some((x) => x.sample.assembly_units);
  const packed = palletPackedTotal(p);
  return (
    <div className="jq-card jq-out-pallet" data-testid={`phone-out-pallet-${p.short_id}`}>
      <div className={`jq-out-pallet-head${state === "all" ? " ticked" : ""}`}>
        {p.riderOnly ? null : (
          <label className="jq-out-tick">
            <input
              type="checkbox"
              aria-label={`Tick pallet ${p.short_id}`}
              checked={state === "all"}
              ref={(el) => { if (el) el.indeterminate = state === "some"; }}
              onChange={(e) => onTick(p, e.target.checked)}
            />
          </label>
        )}
        <div>
          <span className={`jq-out-plt${otherSo ? " other" : ""}`}>{p.short_id}</span>
          {otherSo ? <div className="small jq-muted">{p.palletOrder.sales_order_number}'s pallet</div> : null}
        </div>
        <span className="jq-out-total" data-testid={`phone-out-pallet-total-${p.short_id}`}>
          {fmtQty(p.total)}{anyAssembly ? "" : " pcs"}
        </span>
      </div>
      <div className="jq-out-pallet-body">
        <div className="small jq-muted mt-1">
          {packed - p.total > 1e-9 ? `to send (${fmtQty(packed)} on the pallet, the rest already on papers) · ` : ""}
          {p.parts.length} part{p.parts.length === 1 ? "" : "s"}
          {linked.length ? ` · ticked with ${linked.join(", ")} (a job is on both)` : ""}
          {p.riderOnly ? " · only other orders' pieces are left on it" : ""}
        </div>
        {p.parts.map((part) => {
          const ts = part.lines.map((l) => l.t);
          const vendors = new Set(ts.map((t) => t.vendor_id ?? null));
          return (
            <div className="jq-out-part" key={part.key} data-testid={`phone-out-part-${p.short_id}-${part.sample.product_id}`}>
              <PartImage src={part.sample.image_path} alt={part.sample.assembly_name || part.sample.product_id} />
              <div className="jq-out-part-body">
                <PartName t={part.sample} />
                {part.lines.map((l, i) => <JobLine key={`${l.t.task_id}-${i}`} line={l} />)}
                <FinishLine t={part.sample} mixed={mixed} due={earliest(ts.map((t) => t.due_date))}
                  vendorName={vendors.size > 1 ? "Mixed defaults" : part.sample.vendor_name} />
              </div>
              <div className="jq-out-part-total">
                <span data-testid="phone-out-part-total">{fmtQty(part.total)}</span>
                {part.lines.length > 1 ? <div className="small jq-muted fw-normal">{part.lines.length} jobs</div> : null}
              </div>
            </div>
          );
        })}
      </div>
      <div className="jq-out-pallet-actions">
        {p.pallet_id ? (
          <button type="button" className="btn btn-outline-primary" onClick={() => onFind([p.pallet_id], `Find ${p.short_id}`)}>
            <i className="fas fa-search-location me-1" aria-hidden="true" />Find
          </button>
        ) : null}
        {p.riderOnly ? (
          <button type="button" className="btn btn-outline-dark" disabled={busy} onClick={() => onMakePaper(p.short_id)}>
            Make {p.short_id}'s paper{otherSo ? ` (${p.palletOrder.sales_order_number})` : ""}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** A task with nothing to send on a pallet (on no pallet, or extras the plan never placed): its own tick. */
function LooseRow({ t, selected, mixed, onToggle }) {
  const shareDone = ownShareDone(t);
  const share = t._share || null;
  const age = ageBadge(t.staged_at);
  return (
    <div className="jq-card" data-testid={share ? `phone-out-share-${t.task_id}-${share.sales_order_id}` : `phone-out-loose-${t.task_id}`}>
      <div className="d-flex gap-2 align-items-start">
        {share ? null : (
          <label className="jq-out-tick">
            <input
              type="checkbox"
              aria-label={`Tick job ${t.master_job_num}`}
              checked={selected.has(t.task_id)}
              disabled={shareDone && !selected.has(t.task_id)}
              onChange={() => onToggle(t)}
            />
          </label>
        )}
        <PartImage src={t.image_path} alt={t.assembly_name || t.product_id} />
        <div className="jq-out-part-body">
          <PartName t={t} />
          <div className="jq-out-job">
            <span className="fw-semibold">#{t.master_job_num}</span>
            {t.set_identifier ? <span className="jq-tag">SET {t.set_identifier}</span> : null}
            {share ? <span className="jq-tag jq-tag-warn">↪ from {t.sales_order_number || "another order"}</span> : null}
            {t.cross_so_allocated ? <span className="jq-tag jq-tag-warn">↪ alloc</span> : null}
            <span className={`jq-tag ${age.level === "late" ? "jq-tag-block" : age.level === "warn" ? "jq-tag-warn" : ""}`}>{age.label}</span>
          </div>
          {share ? (
            <div className="small jq-muted">
              {fmtQty(share.qty)} packed for this order, on no pallet yet
              {Number(share.sent) > 0 ? ` · ${fmtQty(share.remaining)} left (${fmtQty(share.sent)} already on papers)` : ""}
            </div>
          ) : (
            <div className="small">
              {fmtQty(t.units != null ? t.units : t.quantity)}{t.assembly_units ? " assemblies" : ""}
              {t.units_remaining != null ? (
                <span className="jq-out-job-notes"> · {fmtQty(t.units_remaining)} left
                  {Array.isArray(t.allocated_away) && t.allocated_away.length ? ` for ${t.sales_order_number || "this order"}` : ""}
                  {Number(t.batched_qty) > 0 ? ` (${fmtQty(t.batched_qty)} already on papers)` : ""}
                </span>
              ) : null}
              {shareDone ? <div className="jq-muted">Nothing of it is left for this order. The rest is packed for {allocatedAwayText(t)}.</div> : null}
              {allocationLines(t).map((line) => <div key={line} className="jq-muted" data-testid="out-alloc-line">{line}</div>)}
              {crossSoShareLine(t) ? <div className="jq-muted">{crossSoShareLine(t)}</div> : null}
              {Number(t.uncoated_extras) > 0 ? (
                <div className="jq-muted">{fmtQty(t.uncoated_extras)} extra{Number(t.uncoated_extras) === 1 ? "" : "s"} stay home (order doesn't ship extras)</div>
              ) : null}
            </div>
          )}
          <FinishLine t={t} mixed={mixed} due={t.due_date} vendorName={t.vendor_name} />
        </div>
      </div>
    </div>
  );
}

export default function ToSendList({ groups, buckets, selected, busy, mixed, inProduction,
  onTickPallet, onToggleTask, onToggleGroup, onFind, onMakePaper }) {
  if (!groups.length) {
    return (
      <>
        <div className="jq-list-state">Nothing is ready to go to a vendor. Jobs appear here as the stations before outsourcing finish.</div>
        <InProduction rows={inProduction} />
      </>
    );
  }
  return (
    <>
      {groups.map((g) => {
        const b = buckets.get(g.key) || { pallets: [], loose: [] };
        const { selectable, allSelected } = groupSelection(g, selected);
        const ids = groupPalletIds(g);
        const who = g.sales_order_number || g.customer_name || "these tasks";
        return (
          <section className="jq-out-group" key={g.key} data-testid={`phone-out-group-${g.key}`}>
            <div className="jq-out-group-head">
              <span className="jq-out-so">{g.sales_order_number || "Internal"}</span>
              <span className="jq-muted">{g.customer_name || (g.sales_order_number ? "" : "no customer")}</span>
              <span className="small jq-muted">
                {b.pallets.length ? `${b.pallets.length} pallet${b.pallets.length !== 1 ? "s" : ""}` : "no pallets"}
              </span>
              <div className="ms-auto d-flex gap-2">
                {ids.length ? (
                  <button type="button" className="btn btn-sm btn-primary" data-testid={`phone-out-find-all-${g.key}`}
                    onClick={() => onFind(ids, `Find the pallets: ${who}`)}>
                    Find all ({ids.length})
                  </button>
                ) : null}
                {selectable.length ? (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onToggleGroup(g, !allSelected)}>
                    {allSelected ? "Untick all" : "Tick all"}
                  </button>
                ) : null}
              </div>
            </div>
            {b.pallets.map((p) => (
              <PalletCard key={p.key} g={g} buckets={b} p={p} selected={selected} busy={busy} mixed={mixed}
                onTick={(pal, checked) => onTickPallet(g, b.pallets, pal.key, checked)} onFind={onFind} onMakePaper={onMakePaper} />
            ))}
            {b.loose.length && b.pallets.length ? <div className="jq-section-title mt-2 mb-1">Not on a pallet yet</div> : null}
            {b.loose.map((t) => (
              <LooseRow key={t._share ? `${t.task_id}-so${t._share.sales_order_id}` : t.task_id} t={t} selected={selected}
                mixed={mixed} onToggle={onToggleTask} />
            ))}
          </section>
        );
      })}
      <InProduction rows={inProduction} />
    </>
  );
}

/** Blocked outsourced tasks still upstream: what is coming (read only). */
function InProduction({ rows }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-2">
      <button type="button" className="btn btn-outline-secondary w-100 text-start" style={{ minHeight: 44 }}
        aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} Still in production ({rows.length})
      </button>
      {open && (
        <div className="mt-2">
          {rows.length === 0 ? <div className="jq-list-state">Nothing upstream right now.</div> : rows.map((t) => (
            <div className="jq-card" key={t.task_id}>
              <PartName t={t} />
              <div className="small jq-muted">#{t.master_job_num}{t.set_identifier ? ` (SET ${t.set_identifier})` : ""} · {fmtQty(t.quantity)} · due {fmtDate(t.due_date)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
